import type { AnyAbility } from '@casl/ability';

import type { TransitionObjectPermissions } from '@/api/client';
import { Action, Resource } from '@/api/client';

/**
 * Whether a per-object ``user_permissions`` block grants ``action`` on
 * ``resource``.
 *
 * Returns ``false`` for an empty/absent list — callers who want the
 * session-CASL fallback should use :func:`resolveObjectPermission` or
 * :func:`useObjectPermission` instead.
 *
 * ``Action.Full`` on the resource satisfies any specific action.
 */
export function hasObjectAction(
  userPermissions: TransitionObjectPermissions[] | null | undefined,
  action: Action,
  resource: Resource
): boolean {
  if (!userPermissions?.length) return false;
  const entry = userPermissions.find((p) => p.resource === resource);
  if (!entry) return false;
  return (entry.actions ?? []).some((a) => a === action || a === Action.Full);
}

/**
 * "Any of" variant — true when the block grants at least one of ``actions``
 * on ``resource``. Useful for capabilities that map to multiple wire actions
 * (e.g. ``canEditTask`` covers both ``Update`` and ``Full``).
 */
export function hasAnyObjectAction(
  userPermissions: TransitionObjectPermissions[] | null | undefined,
  actions: Action[],
  resource: Resource
): boolean {
  if (!userPermissions?.length) return false;
  const entry = userPermissions.find((p) => p.resource === resource);
  if (!entry) return false;
  const granted = entry.actions ?? [];
  return actions.some((a) => granted.some((g) => g === a || g === Action.Full));
}

/**
 * Canonical resolution rule combining session CASL with a per-object
 * ``user_permissions`` block:
 *
 * - Empty/absent block → session ability wins (admin / GLOBAL path, or a role
 *   with no object-authorized resources).
 * - Non-empty block, resource entry present → block is authoritative for that
 *   resource (session ability is ignored).
 * - Non-empty block, resource entry **missing** → treated as *deny*. The
 *   backend only omits an entry when the caller has no access to that
 *   resource (e.g. metadata-only viewer has ``Transitions_Document`` but not
 *   ``Transitions_DocumentContents``); falling back to session ability here
 *   would leak byte-stream affordances to metadata-only users.
 */
export function resolveObjectPermission(
  ability: AnyAbility,
  userPermissions: TransitionObjectPermissions[] | null | undefined,
  action: Action,
  resource: Resource
): boolean {
  if (!userPermissions?.length) {
    return ability.can(action, resource);
  }
  return hasObjectAction(userPermissions, action, resource);
}

/**
 * "Any of" variant of :func:`resolveObjectPermission` — the block must grant
 * at least one of ``actions`` on ``resource`` (or ``Full``); empty block
 * falls back to session ability with the same "any" semantics.
 */
export function resolveAnyObjectPermission(
  ability: AnyAbility,
  userPermissions: TransitionObjectPermissions[] | null | undefined,
  actions: Action[],
  resource: Resource
): boolean {
  if (!userPermissions?.length) {
    return actions.some((a) => ability.can(a, resource));
  }
  return hasAnyObjectAction(userPermissions, actions, resource);
}
